import { useState } from "react"
import { cn } from "@/lib/utils"
import { BackLink } from "@/components/back-link"
import { Button } from "@/components/ui/button"
import { GearIcon } from "@/components/icons"
import { ResponsiveDialog } from "@/components/responsive-dialog"
import {
  FuriganaControls,
  SettingsSwitchRow,
} from "@/components/furigana-controls"
import type { FuriganaMode } from "@/hooks/use-furigana-preference"

interface ReaderSettingsContentProps {
  hasTranslation: boolean
  showTranslation: boolean
  onShowTranslationChange: (show: boolean) => void
  mode: FuriganaMode
  onModeChange: (mode: FuriganaMode) => void
  firstOnly: boolean
  onFirstOnlyChange: (firstOnly: boolean) => void
}

type ReaderSettingsVariant = "popover" | "sheet"

// The settings themselves, shared by the desktop popover and the phone sheet:
// a "Furigana" group (mode + first-only) and, when the story has one, a
// separate "Show translation" group below it. The sheet draws each group as a
// card on the sheet background; the popover is itself the card and separates
// the groups with a hairline.
export function ReaderSettingsContent({
  hasTranslation,
  showTranslation,
  onShowTranslationChange,
  variant = "popover",
  ...furigana
}: ReaderSettingsContentProps & { variant?: ReaderSettingsVariant }) {
  const translation = hasTranslation && (
    <SettingsSwitchRow
      className={
        variant === "sheet"
          ? "min-h-12 rounded-xl bg-card px-4 text-[17px]"
          : "mt-1 border-t border-[var(--surface-hairline,var(--border))] pt-3.5 text-sm"
      }
      label="Show translation"
      checked={showTranslation}
      onCheckedChange={onShowTranslationChange}
    />
  )

  if (variant === "sheet") {
    return (
      <div className="flex flex-col gap-6 pb-6">
        <div className="flex flex-col gap-2">
          <span className="pl-4 text-[13px] text-muted-foreground uppercase">
            Furigana
          </span>
          <div className="overflow-hidden rounded-xl bg-card">
            <FuriganaControls
              {...furigana}
              selectorClassName="px-3 py-2.5"
              rowClassName="ml-4 min-h-12 border-t border-[var(--surface-hairline,var(--border))] pr-4 text-[17px]"
            />
          </div>
        </div>
        {translation}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      <span className="text-xs tracking-widest text-muted-foreground uppercase">
        Furigana
      </span>
      <FuriganaControls {...furigana} rowClassName="text-sm" />
      {translation}
    </div>
  )
}

// Phone layout: the gear in the top bar opens the settings in a bottom sheet.
export function ReaderSettingsSheet(props: ReaderSettingsContentProps) {
  const [open, setOpen] = useState(false)
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={setOpen}
      title="Reading settings"
      className="bg-background"
      trigger={
        <Button
          variant="ghost"
          size="icon"
          className="size-11 text-primary hover:text-primary"
          aria-label="Reading settings"
        >
          <GearIcon className="size-6" />
        </Button>
      }
    >
      <ReaderSettingsContent variant="sheet" {...props} />
    </ResponsiveDialog>
  )
}

interface SettingsCogProps {
  open: boolean
  onToggle: () => void
  /** id of the panel this cog controls (for aria-controls). */
  controlsId: string
  className?: string
}

// The cog that toggles the reader settings popover, sitting in the reader's
// top bar next to the back link.
export function SettingsCog({
  open,
  onToggle,
  controlsId,
  className,
}: SettingsCogProps) {
  return (
    <Button
      variant="outline"
      size="icon"
      className={cn(
        "shrink-0 bg-[var(--surface-input,var(--card))]",
        className
      )}
      aria-label="Reading settings"
      aria-expanded={open}
      aria-controls={controlsId}
      onClick={onToggle}
    >
      <GearIcon
        className={cn(
          "size-5 transition-transform duration-300",
          open && "rotate-90"
        )}
      />
    </Button>
  )
}

interface ReaderSettingsOverlayProps extends ReaderSettingsContentProps {
  id: string
  open: boolean
  className?: string
}

// Popover anchored below the cog: it fades in over the content below without
// pushing anything down. The parent row must be `relative`.
export function ReaderSettingsOverlay({
  id,
  open,
  className,
  ...content
}: ReaderSettingsOverlayProps) {
  return (
    <div
      id={id}
      className={cn(
        "absolute top-full right-0 z-10 mt-3 w-full transition-opacity duration-300 ease-out sm:w-80",
        open ? "opacity-100" : "pointer-events-none opacity-0",
        className
      )}
    >
      <div className="rounded-2xl border border-[var(--surface-hairline,var(--border))] bg-card p-[18px] shadow-lg">
        <ReaderSettingsContent {...content} />
      </div>
    </div>
  )
}

interface ReaderHeaderProps extends ReaderSettingsContentProps {
  /** Show the settings cog + popover only once the story has loaded. */
  showSettings: boolean
  className?: string
}

// Desktop top bar for the reader routes: the back link with the settings cog
// on the same row and the settings popover below the cog. The story route
// shows it on desktop only (the phone layout uses Layout's back bar and
// `ReaderSettingsSheet`); the admin viewer shows it at every width.
export function ReaderHeader({
  showSettings,
  className,
  ...content
}: ReaderHeaderProps) {
  const [open, setOpen] = useState(false)
  return (
    <div className={cn("mb-8", className)}>
      <div className="relative flex items-center justify-between gap-4">
        <BackLink to="/stories" />
        {showSettings && (
          <>
            <SettingsCog
              open={open}
              onToggle={() => setOpen((o) => !o)}
              controlsId="reader-settings"
            />
            <ReaderSettingsOverlay
              id="reader-settings"
              open={open}
              {...content}
            />
          </>
        )}
      </div>
    </div>
  )
}
